import React, { useState, useEffect } from 'react';
import {
  Table,
  Box,
  SpaceBetween,
  Button,
  Header,
  Badge,
  PropertyFilter,
  Pagination,
  CollectionPreferences,
  Link,
  ButtonDropdown,
  Modal,
  FormField,
  Input,
  Select,
  Flashbar,
  Spinner
} from '@cloudscape-design/components';
import { s3Service } from '../services/s3Service';
import { useUserPreferences } from '../hooks/useUserPreferences';
import { extractReviewState, isValidationInFlight } from '../utils/reviewState';

const STORAGE_KEY = 'chronos_table_preferences';

const IDENTIFIER_OPTIONS = [
  { label: 'Fund Request ID', value: 'fundRequest' },
  { label: 'Benefit Application ID', value: 'benefitApplication' },
];

const loadStoredPreferences = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved) : null;
  } catch { return null; }
};

const saveStoredPreferences = (prefs) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  } catch { /* ignore quota errors */ }
};

const STATUS_COLORS = {
  'RECOMMEND_APPROVAL': 'green',
  'RECOMMEND_REJECTION': 'red',
  'NEEDS_REVIEW': 'blue',
  // Backward compatibility
  'recommend approval': 'green',
  'recommend rejection': 'red',
  'needs review': 'blue',
  'approved': 'green',
  'conditional approval': 'blue',
  'rejected': 'red',
  'pending': 'grey',
  'processing': 'blue',
  'validating': 'blue',
  'tech review': 'blue',
  'business review': 'blue',
  'pending approval': 'grey',
  // Blind review (MAP): the result is withheld until a reviewer records their
  // own decision, so the row shows no outcome color.
  'blind review pending': 'grey',
  // WMP and IW decisions (as received from Lambda after underscore→space normalization)
  'approve': 'green',
  'reject': 'red',
  'escalate': 'blue',
  'flag for manual review': 'blue',
};

const FundingRequestTable = ({ fundingRequests, totalUnfilteredCount = 0, loading, onRefresh, onViewRequest, queueMode = false, daysFilter = null, onDaysFilterChange = null, initialTableView = null, onTableViewChange = null }) => {
  console.log('FundingRequestTable render:', { 
    fundingRequestsCount: fundingRequests?.length, 
    loading, 
    queueMode,
    fundingRequests: fundingRequests 
  });
  
  // Load user preferences
  const { pageSize: savedPageSize, updatePageSize, loading: prefsLoading } = useUserPreferences();
  
  const [filterQuery, setFilterQuery] = useState(initialTableView?.filterQuery || { tokens: [], operation: 'and' });
  const [currentPageIndex, setCurrentPageIndex] = useState(initialTableView?.currentPageIndex || 1);

  // When daysFilter prop changes (e.g. Show All toggled), sync the days token in filterQuery
  useEffect(() => {
    setFilterQuery(prev => {
      const otherTokens = prev.tokens.filter(t => t.propertyKey !== 'days');
      if (daysFilter != null) {
        return { ...prev, tokens: [...otherTokens, { propertyKey: 'days', operator: '=', value: String(daysFilter) }] };
      }
      return { ...prev, tokens: otherTokens };
    });
  }, [daysFilter]);

  // Handle filter changes from PropertyFilter — extract days token and notify parent
  const handleFilterChange = ({ detail }) => {
    setFilterQuery(detail);
    setCurrentPageIndex(1);

    const daysToken = detail.tokens.find(t => t.propertyKey === 'days');
    const newDays = daysToken ? parseInt(daysToken.value, 10) || null : null;
    if (onDaysFilterChange && newDays !== daysFilter) {
      onDaysFilterChange(newDays);
    }
  };
  const [showNewFRModal, setShowNewFRModal] = useState(false);
  const [newFRId, setNewFRId] = useState('');
  const [selectedIdType, setSelectedIdType] = useState(IDENTIFIER_OPTIONS[0]);
  const [notifications, setNotifications] = useState([]);
  const resetNewFRModal = () => {
    setShowNewFRModal(false);
    setNewFRId('');
    setSelectedIdType(IDENTIFIER_OPTIONS[0]);
  };
  const [sortingColumn, setSortingColumn] = useState(initialTableView?.sortingColumn || { sortingField: 'processed_date' });
  const [isDescending, setIsDescending] = useState(initialTableView?.isDescending ?? true);

  // Report the table view (filter/sort/page) up so the page can snapshot it.
  useEffect(() => {
    if (onTableViewChange) onTableViewChange({ filterQuery, sortingColumn, isDescending, currentPageIndex });
  }, [filterQuery, sortingColumn, isDescending, currentPageIndex, onTableViewChange]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Pending validations - persisted to localStorage
  const [pendingValidations, setPendingValidations] = useState(() => {
    try {
      const saved = localStorage.getItem('chronos_pending_validations');
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });
  
  // Persist pending validations to localStorage
  useEffect(() => {
    localStorage.setItem('chronos_pending_validations', JSON.stringify(pendingValidations));
  }, [pendingValidations]);
  
  // Resume polling for any pending validations on mount
  useEffect(() => {
    pendingValidations.forEach(pv => {
      if (pv.status === 'validating') {
        pollValidationStatus(pv.id);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [preferences, setPreferences] = useState(() => {
    const stored = loadStoredPreferences();
    return {
      pageSize: savedPageSize || 10,
      visibleContent: stored?.visibleContent || (queueMode
        ? ['id', 'projectName', 'requestor', 'program', 'status', 'lastUpdated', 'retryCount', 'actions']
        : ['id', 'status', 'program', 'requestor', 'customer', 'arr', 'migrationPhase', 'projectName', 'actions'])
    };
  });

  // Update preferences when saved page size changes (only on initial load)
  useEffect(() => {
    if (savedPageSize && !prefsLoading) {
      setPreferences(prev => ({
        ...prev,
        pageSize: savedPageSize
      }));
    }
  }, [savedPageSize, prefsLoading]);

  // Create placeholder rows for pending validations (not yet in fundingRequests)
  const pendingRows = pendingValidations
    .filter(pv => !fundingRequests.find(fr => fr.id === pv.id))
    .map(pv => ({
      id: pv.id,
      requestor: '-',
      customer: '-',
      arr: null,
      migrationPhase: '-',
      status: pv.status === 'failed' ? 'VALIDATION_FAILED' : pv.status === 'timeout' ? 'TIMEOUT' : 'VALIDATING',
      processed_date: pv.submittedAt,
      _isPending: true,
      _pendingStatus: pv.status
    }));

  // Merge pending rows at the top
  const allItems = [...pendingRows, ...fundingRequests];

  const filteredItems = filterQuery.tokens.length === 0 ? allItems : allItems.filter(item => {
    // Exclude 'days' token from client-side filtering (handled server-side)
    const clientTokens = filterQuery.tokens.filter(t => t.propertyKey !== 'days');
    if (clientTokens.length === 0) return true;
    const results = clientTokens.map(token => {
      // Numeric comparison for quantitative properties (ARR).
      if (token.propertyKey === 'arr') {
        const itemNum = Number(item.arr);
        const tokenNum = Number(String(token.value).replace(/[^0-9.-]/g, ''));
        if (Number.isNaN(itemNum) || Number.isNaN(tokenNum)) return false;
        switch (token.operator) {
          case '=': return itemNum === tokenNum;
          case '!=': return itemNum !== tokenNum;
          case '>': return itemNum > tokenNum;
          case '>=': return itemNum >= tokenNum;
          case '<': return itemNum < tokenNum;
          case '<=': return itemNum <= tokenNum;
          default: return true;
        }
      }
      // Status is stored in mixed formats (e.g. "recommend approval"); compare
      // the normalized form the column displays (UPPER_SNAKE) so options match.
      if (token.propertyKey === 'status') {
        const norm = String(item.status || '').toUpperCase().replace(/ /g, '_');
        const tv = String(token.value).toUpperCase().replace(/ /g, '_');
        return token.operator === '!=' ? norm !== tv : norm === tv;
      }
      const val = String(item[token.propertyKey] || '').toLowerCase();
      const tokenVal = String(token.value).toLowerCase();
      switch (token.operator) {
        case '=': return val === tokenVal;
        case '!=': return val !== tokenVal;
        case ':': return val.includes(tokenVal);
        case '!:': return !val.includes(tokenVal);
        default: return true;
      }
    });
    return filterQuery.operation === 'or' ? results.some(Boolean) : results.every(Boolean);
  });

  // Sort filtered items
  const sortedItems = [...filteredItems].sort((a, b) => {
    const field = sortingColumn.sortingField;
    if (!field) return 0;
    
    const aVal = a[field];
    const bVal = b[field];
    
    // Handle null/undefined
    if (aVal == null && bVal == null) return 0;
    if (aVal == null) return 1;
    if (bVal == null) return -1;
    
    // Compare values
    let comparison = 0;
    if (typeof aVal === 'string') {
      comparison = aVal.localeCompare(bVal);
    } else {
      comparison = aVal - bVal;
    }
    
    return isDescending ? -comparison : comparison;
  });

  // Clamp the page inline so an out-of-range page renders page 1 immediately
  // (no empty-table flash); the effect below syncs the state so the pagination
  // control follows.
  const pageCount = Math.max(1, Math.ceil(sortedItems.length / preferences.pageSize));
  const effectivePageIndex = sortedItems.length > 0 && currentPageIndex > pageCount ? 1 : currentPageIndex;
  const paginatedItems = sortedItems.slice(
    (effectivePageIndex - 1) * preferences.pageSize,
    effectivePageIndex * preferences.pageSize
  );

  // Sync the state to the clamped page so the pagination control follows. Skip
  // while empty so a restored page survives the initial load.
  useEffect(() => {
    if (sortedItems.length === 0) return;
    if (currentPageIndex > pageCount) setCurrentPageIndex(1);
  }, [sortedItems.length, pageCount, currentPageIndex]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const handleRowClick = (item) => {
    if (onViewRequest) {
      onViewRequest(item.id);
    }
  };

  const addNotification = (type, header, content) => {
    const id = Date.now().toString();
    setNotifications([{
      type,
      header,
      content,
      dismissible: true,
      dismissLabel: "Dismiss",
      onDismiss: () => setNotifications([]),
      id
    }]);
  };

  const addPendingValidation = (frId) => {
    setPendingValidations(prev => {
      if (prev.find(p => p.id === frId)) return prev;
      return [{ id: frId, status: 'validating', submittedAt: new Date().toISOString() }, ...prev];
    });
  };

  const updatePendingStatus = (frId, status) => {
    setPendingValidations(prev => prev.map(p => p.id === frId ? { ...p, status } : p));
  };

  const removePending = (frId) => {
    setPendingValidations(prev => prev.filter(p => p.id !== frId));
  };

  const pollValidationStatus = (frId) => {
    let attempts = 0;
    // 120 x 5000ms = 600s. MAP validations measured in prod over
    // 2026-08-13..17 (n=301) run p50 114s / p90 177s / p99 444s, so the
    // previous 60 x 2000ms = 120s ceiling expired before 44% of runs
    // finished and reported a healthy validation as a timeout. See the
    // handleValidate poll below for the full reasoning.
    const maxAttempts = 120;
    const pollInterval = 5000;

    const check = async () => {
      try {
        const statusData = await s3Service.getReviewStatus(frId);
        const reviewState = statusData.review_state || statusData.fundRequests?.[0]?.review_state;
        attempts++;

        if (reviewState === '3-Completed') {
          removePending(frId);
          setIsSubmitting(false);
          if (onRefresh) setTimeout(() => onRefresh(), 1500);
          return;
        } else if (reviewState === '4-Failed') {
          updatePendingStatus(frId, 'failed');
          setIsSubmitting(false);
          // Auto-remove failed after 30 seconds
          setTimeout(() => removePending(frId), 30000);
          return;
        } else if (attempts >= maxAttempts) {
          updatePendingStatus(frId, 'timeout');
          setIsSubmitting(false);
          setTimeout(() => removePending(frId), 30000);
          return;
        } else {
          setTimeout(check, pollInterval);
        }
      } catch (error) {
        attempts++;
        if (attempts >= maxAttempts) {
          updatePendingStatus(frId, 'failed');
          setIsSubmitting(false);
          setTimeout(() => removePending(frId), 30000);
        } else {
          setTimeout(check, pollInterval);
        }
      }
    };

    setTimeout(check, pollInterval);
  };

  const handleReviewNewFR = async () => {
    const identifierLabel = selectedIdType.value === 'benefitApplication'
      ? 'Benefit Application ID'
      : 'Fund Request ID';
    if (!newFRId.trim()) {
      addNotification('error', 'Missing Information', `Please enter a ${identifierLabel}`);
      return;
    }

    const frId = newFRId.trim();
    if (selectedIdType.value === 'benefitApplication') {
      setIsSubmitting(true);
      resetNewFRModal();
      addNotification('info', 'Searching', `Searching for ${frId}...`);
      try {
        const matches = await s3Service.getFundingRequestsByBenefitApplicationId(frId);
        if (matches.length === 0) {
          addNotification(
            'warning',
            'No Reviewed Fund Requests Found',
            `No reviewed fund requests were found for Benefit Application ID ${frId}.`
          );
        } else if (matches.length === 1 && onViewRequest) {
          onViewRequest(matches[0].id);
        } else {
          addNotification(
            'info',
            'Multiple Fund Requests Found',
            <div>
              <div>Benefit Application ID {frId} matches multiple fund requests:</div>
              <ul>
                {matches.map(match => (
                  <li key={match.id}>
                    <Link variant="primary" onFollow={() => onViewRequest?.(match.id)}>
                      {match.id}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          );
        }
      } catch (error) {
        addNotification('error', 'Search Failed', error.message);
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    console.log('Starting validation process for FR:', frId);

    setIsSubmitting(true);
    resetNewFRModal();
    addNotification('info', 'Processing', `Validating ${frId}...`);

    let existsInValidations = false;
    let existsInQueue = false;

    // Step 1: Check if FR exists in validations table (already completed)
    try {
      console.log('Checking if FR already exists in validations...');
      await s3Service.getFundingRequestById(frId);
      existsInValidations = true;
      console.log('FR found in validations - already reviewed');
    } catch (error) {
      const errorMsg = error.message.toLowerCase();
      console.log('Validation check result:', errorMsg);
      
      if (errorMsg.includes('404') || errorMsg.includes('not found')) {
        console.log('FR not found in validations (good - new FR)');
      } else {
        console.warn('Validation check failed:', error.message);
        // Continue anyway - don't block the process
      }
    }

    // Step 2: Check if FR is currently IN FLIGHT in the review-status table.
    //
    // Gate on the review_state VALUE, not on row existence. chronos_review_state
    // keeps its row forever once an FR has run, including terminal 3-Completed /
    // 4-Failed states, so an existence-only check reported a long-dead failed run
    // as "currently being processed" and refused to re-submit it.
    //
    // invoke_validation's conditional write is the authoritative guard and it
    // ALREADY permits a re-queue on those two terminal states
    // (attribute_not_exists(FR_id) OR review_state = '3-Completed' OR
    // review_state = '4-Failed'). Gating on existence here was therefore stricter
    // than the backend contract, and it deadlocked any FR that failed BEFORE a
    // chronos_validated_fundrequests row was written: the grid had no record to
    // open, so the "Already Reviewed" escape hatch below did not apply either,
    // and the modal was the only way in. Anything non-terminal still blocks here;
    // for any state this does not recognise the backend 409 remains the guard.
    try {
      console.log('Checking if FR is currently in queue...');
      const statusData = await s3Service.getReviewStatus(frId);
      existsInQueue = isValidationInFlight(statusData);
      console.log(
        `FR found in queue with review_state=${extractReviewState(statusData)}, inFlight=${existsInQueue}`
      );
    } catch (error) {
      const errorMsg = error.message.toLowerCase();
      console.log('Queue check result:', errorMsg);
      
      if (errorMsg.includes('404') || errorMsg.includes('not found')) {
        console.log('FR not found in queue (good - not processing)');
      } else {
        console.warn('Queue check failed:', error.message);
        // Continue anyway - don't block the process
      }
    }

    // Handle cases where FR already exists
    if (existsInValidations) {
      setIsSubmitting(false);
      addNotification(
        'warning',
        'Fund Request Already Reviewed',
        <span>
          {frId} has already been reviewed. <Link variant="primary" onFollow={() => onViewRequest(frId)}>Click here to view the reviewed record</Link>. To re-validate, open the record and click the "Validate" button.
        </span>
      );
      setNewFRId('');
      return;
    }

    if (existsInQueue) {
      setIsSubmitting(false);
      addNotification(
        'info',
        'Fund Request In Progress',
        `${frId} is already being processed.`
      );
      setNewFRId('');
      return;
    }

    // Step 3: Trigger validation (FR appears to be new)
    try {
      console.log('Triggering validation for new FR:', frId, 'ID Type: fundRequest');
      await s3Service.triggerValidation(frId, 'fundRequest');
      
      addNotification(
        'info',
        'Validation Triggered',
        `Validation has been triggered for Fund Request ${frId}.`
      );
      
      setNewFRId('');
      
      // Auto-filter to show the submitted FR (preserve days token if present)
      setFilterQuery(prev => {
        const daysToken = prev.tokens.find(t => t.propertyKey === 'days');
        const tokens = [{ propertyKey: 'id', operator: ':', value: frId }];
        if (daysToken) tokens.push(daysToken);
        return { tokens, operation: 'and' };
      });
      
      // Add placeholder row to table
      addPendingValidation(frId);
      
      if (onRefresh) {
        onRefresh();
      }

      // Start polling via the shared function
      pollValidationStatus(frId);
      
    } catch (validationError) {
      console.error('Validation trigger failed:', validationError);
      setIsSubmitting(false);
      addNotification('error', 'Validation Failed', validationError.message);
    }
  };

  const handleReviewAgain = async (fundRequestId) => {
    try {

      // Trigger validation
      await s3Service.triggerValidation(fundRequestId);
      
      addNotification('info', 'Validation In Progress', `Validation triggered for ${fundRequestId}. Checking status...`);
      
      console.log('Validation triggered, starting status polling...');
      
      // Start polling for status
      let attempts = 0;
      // 120 x 5000ms = 600s total. The previous 60 x 2000ms gave up after
      // 120s, but MAP validations measured in prod over 2026-08-13..17
      // (n=301) run p50 114s / p90 177s / p99 444s — 44% of runs outlived
      // the ceiling. Every one of those surfaced "Status Check Timeout" on a
      // validation that was still running fine, users re-clicked Validate,
      // invoke_validation's conditional write answered the still-in-flight
      // FR with a 409, and triggerValidation surfaced that verbatim as a red
      // "Validation Failed" toast. On 2026-08-14 one reviewer hit that 409
      // 15 times across 6 MAP FRs; all 6 completed successfully.
      //
      // 600s covers p99 plus dispatch queueing. Polling every 5s instead of
      // 2s keeps this at 120 review-status calls per validation versus the
      // previous 60, so 5x the coverage costs 2x the requests.
      const maxAttempts = 120;
      const pollInterval = 5000;
      
      const checkStatus = async () => {
        try {
          const statusData = await s3Service.getReviewStatus(fundRequestId);
          console.log('Status check response:', statusData);
          const reviewState = statusData.review_state || statusData.fundRequests?.[0]?.review_state;
          console.log(`Review state: ${reviewState}, Attempt: ${attempts + 1}/${maxAttempts}`);
          
          attempts++;
          
          if (reviewState === '3-Completed') {
            addNotification('success', 'Validation Completed', `Validation completed successfully! Refreshing data...`);
            // Brief delay to allow DynamoDB eventual consistency to settle
            if (onRefresh) {
              setTimeout(() => onRefresh(), 1500);
            }
            return;
          } else if (reviewState === '4-Failed') {
            addNotification('error', 'Validation Failed', `Validation failed. Please check the details and try again.`);
            if (onRefresh) {
              setTimeout(() => onRefresh(), 1500);
            }
            return;
          } else if (attempts >= maxAttempts) {
            addNotification('warning', 'Status Check Timeout', `Validation is taking longer than expected. Please refresh manually to see results.`);
            return;
          } else {
            // Still in progress, continue polling
            const statusText = reviewState === '1-Pending' ? 'Pending' : reviewState === '2-In Progress' ? 'In Progress' : reviewState;
            console.log(`Validation status: ${statusText} (attempt ${attempts}/${maxAttempts})`);
            setTimeout(checkStatus, pollInterval);
          }
        } catch (error) {
          console.error('Error checking status:', error);
          if (attempts >= maxAttempts) {
            addNotification('error', 'Status Check Failed', `Could not verify validation status. Please refresh manually.`);
          } else {
            setTimeout(checkStatus, pollInterval);
          }
        }
      };
      
      // Start checking status after a brief delay
      setTimeout(checkStatus, pollInterval);
      
    } catch (error) {
      addNotification('error', 'Validation Failed', error.message);
    }
  };

  const allColumnDefinitions = [
    {
      id: 'id',
      header: 'Request ID',
      cell: item => item._isPending ? (
        <Box color="text-status-inactive">{item.id}</Box>
      ) : (
        <Link
          onFollow={() => handleRowClick(item)}
          variant="primary"
        >
          {item.id}
        </Link>
      ),
      sortingField: 'id',
      width: 440,
      minWidth: 400
    },
    {
      id: 'requestor',
      header: 'Partner',
      cell: item => item.requestor,
      width: 140,
      minWidth: 100
    },
    {
      id: 'customer',
      header: 'Customer',
      cell: item => item.customer || '-',
      width: 140,
      minWidth: 100
    },
    {
      id: 'arr',
      header: 'ARR',
      cell: item => item.arr ? formatCurrency(item.arr) : '-',
      sortingField: 'arr',
      width: 130,
      minWidth: 100
    },
    {
      id: 'migrationPhase',
      header: 'Phase',
      cell: item => item.migrationPhase || '-',
      width: 110,
      minWidth: 90
    },
    {
      id: 'status',
      header: 'Status',
      cell: item => {
        if (item._isPending) {
          if (item._pendingStatus === 'validating') {
            return (
              <Box color="text-status-info">
                <SpaceBetween direction="horizontal" size="xs">
                  <Spinner size="normal" />
                  Validating...
                </SpaceBetween>
              </Box>
            );
          }
          if (item._pendingStatus === 'failed') {
            return <Badge color="red">VALIDATION_FAILED</Badge>;
          }
          if (item._pendingStatus === 'timeout') {
            return <Badge color="grey">TIMEOUT</Badge>;
          }
        }
        const displayStatus = item.status?.toUpperCase()?.replace(/ /g, '_') || item.status;
        return (
          <Badge color={STATUS_COLORS[item.status] || STATUS_COLORS[displayStatus] || 'grey'}>
            {displayStatus}
          </Badge>
        );
      },
      width: 200,
      minWidth: 180
    },
    {
      id: 'validationDate',
      header: 'Validated',
      cell: item => item.processed_date ? new Date(item.processed_date).toLocaleDateString('en-US', {
        year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
      }) : '-',
      sortingField: 'processed_date',
      width: 180,
      minWidth: 150
    },
    {
      id: 'projectName',
      header: 'Project',
      cell: item => item.projectName,
      width: 180,
      minWidth: 150
    },
    // Queue mode only columns
    {
      id: 'program',
      header: 'Program',
      cell: item => item.program || '-',
      width: 90,
      minWidth: 80
    },
    {
      id: 'lastUpdated',
      header: 'Updated',
      cell: item => item.lastUpdated ? new Date(item.lastUpdated).toLocaleDateString() : '-',
      width: 110,
      minWidth: 100
    },
    {
      id: 'retryCount',
      header: 'Retries',
      cell: item => item.retryCount !== undefined ? item.retryCount : '-',
      width: 80
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: item => (
        <div style={{ position: 'relative', zIndex: 1 }}>
          <ButtonDropdown
            variant="inline-icon"
            ariaLabel={`Actions for ${item.id}`}
            expandToViewport={true}
            items={queueMode ? [
              {
                id: 'details',
                text: 'View Status'
              }
            ] : [
              {
                id: 'details',
                text: 'Details'
              }
            ]}
            onItemClick={async (e) => {
              e.stopPropagation();
              const { id: actionId } = e.detail;
              if (actionId === 'details') {
                if (onViewRequest) {
                  onViewRequest(item.id);
                }
              }
            }}
          />
        </div>
      ),
      minWidth: 120
    }
  ];

  // Filter columns based on queue mode
  const queueModeColumns = ['id', 'projectName', 'requestor', 'program', 'status', 'lastUpdated', 'retryCount', 'actions'];
  const dashboardColumns = ['id', 'status', 'program', 'requestor', 'customer', 'arr', 'migrationPhase', 'validationDate', 'projectName', 'actions'];
  const columnDefinitions = queueMode 
    ? queueModeColumns.map(id => allColumnDefinitions.find(col => col.id === id)).filter(Boolean)
    : dashboardColumns.map(id => allColumnDefinitions.find(col => col.id === id)).filter(Boolean);

  return (
    <>
      <Flashbar items={notifications} />
      <Modal
        visible={showNewFRModal}
        onDismiss={resetNewFRModal}
        header={selectedIdType.value === 'benefitApplication'
          ? 'Find Fund Requests'
          : 'Review New Fund Request'}
        closeAriaLabel="Close"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button
                variant="link"
                onClick={resetNewFRModal}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleReviewNewFR}
                disabled={isSubmitting}
                loading={isSubmitting}
              >
                {selectedIdType.value === 'benefitApplication' ? 'Search' : 'Submit'}
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <SpaceBetween size="l">
          <FormField label="Identifier Type">
            <Select
              selectedOption={selectedIdType}
              onChange={({ detail }) => setSelectedIdType(detail.selectedOption)}
              options={IDENTIFIER_OPTIONS}
            />
          </FormField>
          <FormField
            label={selectedIdType.label}
            description={selectedIdType.value === 'benefitApplication'
              ? 'Enter the Benefit Application ID (e.g., benappl-xxxxxxxx)'
              : 'Enter the Fund Request ID (e.g., FR-MAP-CASH-12345)'}
          >
            <Input
              value={newFRId}
              onChange={({ detail }) => setNewFRId(detail.value)}
              placeholder={selectedIdType.value === 'benefitApplication'
                ? 'benappl-xxxxxxxx'
                : 'FR-XXXXX-XXXXX'}
            />
          </FormField>
        </SpaceBetween>
      </Modal>
      <Table
      columnDefinitions={columnDefinitions}
      visibleColumns={preferences.visibleContent}
      items={paginatedItems}
      loading={loading}
      loadingText="Loading funding requests"
      trackBy="id"
      resizableColumns
      sortingColumn={sortingColumn}
      sortingDescending={isDescending}
      onSortingChange={({ detail }) => {
        setSortingColumn(detail.sortingColumn);
        setIsDescending(detail.isDescending);
      }}
      stickyHeader
      variant="full-page"
      empty={
        <Box textAlign="center" color="inherit">
          <b>No fund requests found</b>
          <Box padding={{ bottom: 's' }} variant="p" color="inherit">
            {filterQuery.tokens.length > 0
              ? 'No fund requests match your filters. Try adjusting the filter criteria.'
              : totalUnfilteredCount > 0
              ? 'No fund requests match the selected program scope. Try selecting more programs.'
              : 'No fund requests to display. Use "Review New FR" to validate a new fund request.'}
          </Box>
        </Box>
      }
      filter={
          <PropertyFilter
          query={filterQuery}
          onChange={handleFilterChange}
          filteringProperties={[
            { key: 'id', propertyLabel: 'Request ID', groupValuesLabel: 'Request ID values', operators: ['=', '!=', ':', '!:'] },
            { key: 'benefitApplicationId', propertyLabel: 'Benefit Application ID', groupValuesLabel: 'Benefit Application ID values', operators: ['=', '!=', ':', '!:'] },
            { key: 'program', propertyLabel: 'Program', groupValuesLabel: 'Program values', operators: ['=', '!='] },
            { key: 'requestor', propertyLabel: 'Partner', groupValuesLabel: 'Partner values', operators: ['=', '!=', ':', '!:'] },
            { key: 'customer', propertyLabel: 'Customer', groupValuesLabel: 'Customer values', operators: ['=', '!=', ':', '!:'] },
            { key: 'arr', propertyLabel: 'ARR', groupValuesLabel: 'ARR values', operators: ['=', '!=', '>', '>=', '<', '<='] },
            { key: 'migrationPhase', propertyLabel: 'Phase', groupValuesLabel: 'Phase values', operators: ['=', '!='] },
            { key: 'status', propertyLabel: 'Status', groupValuesLabel: 'Status values', operators: ['=', '!='] },
            { key: 'projectName', propertyLabel: 'Project', groupValuesLabel: 'Project values', operators: ['=', '!=', ':', '!:'] },
            { key: 'days', propertyLabel: 'Days', groupValuesLabel: 'Days values', operators: ['='] },
          ]}
          filteringOptions={[
            { propertyKey: 'program', value: 'MAP' },
            { propertyKey: 'program', value: 'WMP' },
            { propertyKey: 'program', value: 'IW' },
            { propertyKey: 'status', value: 'RECOMMEND_APPROVAL' },
            { propertyKey: 'status', value: 'RECOMMEND_REJECTION' },
            { propertyKey: 'status', value: 'NEEDS_REVIEW' },
            { propertyKey: 'days', value: '7' },
            { propertyKey: 'days', value: '30' },
            { propertyKey: 'days', value: '90' },
            { propertyKey: 'days', value: '180' },
            { propertyKey: 'days', value: '365' },
          ]}
          filteringPlaceholder="Filter fund requests"
          i18nStrings={{
            filteringAriaLabel: 'Filter fund requests',
            dismissAriaLabel: 'Dismiss',
            filteringPlaceholder: 'Filter fund requests',
            groupValuesText: 'Values',
            groupPropertiesText: 'Properties',
            operatorsText: 'Operators',
            operationAndText: 'and',
            operationOrText: 'or',
            operatorContainsText: 'Contains',
            operatorDoesNotContainText: 'Does not contain',
            operatorEqualsText: 'Equals',
            operatorDoesNotEqualText: 'Does not equal',
            editTokenHeader: 'Edit filter',
            propertyText: 'Property',
            operatorText: 'Operator',
            valueText: 'Value',
            cancelActionText: 'Cancel',
            applyActionText: 'Apply',
            allPropertiesLabel: 'All properties',
            tokenLimitShowMore: 'Show more',
            tokenLimitShowFewer: 'Show fewer',
            clearFiltersText: 'Clear filters',
            removeTokenButtonAriaLabel: (token) => `Remove filter, ${token.propertyKey} ${token.operator} ${token.value}`,
            enteredTextLabel: (text) => `Use: "${text}"`,
          }}
        />
      }
      header={
        <Header
          counter={`(${allItems.length})`}
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              <Button
                iconName="refresh"
                variant="icon"
                ariaLabel="Refresh"
                onClick={onRefresh}
              />
              {!queueMode && (
                <Button
                  variant="primary"
                  onClick={() => setShowNewFRModal(true)}
                >
                  Review New FR
                </Button>
              )}
            </SpaceBetween>
          }
        >
          {queueMode ? 'Fund Request Queue' : 'Validated Fund Requests'}
        </Header>
      }
      pagination={
        <Pagination
          currentPageIndex={currentPageIndex}
          onChange={({ detail }) => setCurrentPageIndex(detail.currentPageIndex)}
          pagesCount={Math.ceil(filteredItems.length / preferences.pageSize)}
        />
      }
      preferences={
        <CollectionPreferences
          title="Preferences"
          confirmLabel="Confirm"
          cancelLabel="Cancel"
          preferences={preferences}
          onConfirm={({ detail }) => {
            setPreferences(detail);
            saveStoredPreferences({ visibleContent: detail.visibleContent });
            // Save page size preference if it changed
            if (detail.pageSize !== preferences.pageSize) {
              updatePageSize(detail.pageSize).catch(err => {
                console.error('Failed to save page size preference:', err);
                // Revert to previous value on error
                setPreferences(prev => ({
                  ...prev,
                  pageSize: preferences.pageSize
                }));
              });
            }
          }}
          pageSizePreference={{
            title: 'Page size',
            options: [
              { value: 10, label: '10 items' },
              { value: 25, label: '25 items' },
              { value: 50, label: '50 items' },
              { value: 100, label: '100 items' }
            ]
          }}
          visibleContentPreference={{
            title: 'Select visible columns',
            options: [
              {
                label: 'Main properties',
                options: columnDefinitions.map(({ id, header }) => ({
                  id,
                  label: header,
                  editable: id !== 'id'
                }))
              }
            ]
          }}
        />
      }
    />
    </>
  );
};

export default FundingRequestTable;